<template>
	<section class="rewarded-popup">
		<div class="rewarded-popup__text" v-html="rewardText" />
		<Tooltip :disabled="adsAvailable" :placement="Placements.BOTTOM">
			<template #element>
				<div>
					<AppTextButton
						class="rewarded-popup__btn"
						:disabled="!adsAvailable"
						:type="StatusTypes.PRIMARY"
						:text="translate('button.watch_ad').toUpperCase()"
						@click="watchAd"
					/>
				</div>
			</template>
			<template #tooltip>
				<span>
					{{ translate("ui.no_ad_available") }}
				</span>
			</template>
		</Tooltip>
		<div class="rewarded-popup__btn-divider">
			<DecorativeTextDivider :label="translate('ui.or')" />
		</div>
		<AppTextButton
			class="chest-popup__btn"
			:type="
				resourcesStore.adSkipper
					? StatusTypes.SUCCESS
					: StatusTypes.WARNING
			"
			:text="
				translate(
					resourcesStore.adSkipper
						? 'button.skip_ad'
						: `button.buy_${ResourceType.AD_SKIPPER}`,
				)
			"
			@click="useAdSkipper"
		/>
		<div class="rewarded-popup__skippers">
			{{ `${translate(`ui.${ResourceType.AD_SKIPPER}_left`)}: ` }}
			<span>{{ formatNumber(resourcesStore.adSkipper) }}</span>
			<span :class="`icon-${ResourceType.AD_SKIPPER}-16`" />
		</div>
	</section>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import DecorativeTextDivider from "@/components/SVG/DecorativeTextDivider.vue";
import Tooltip from "@/components/Tooltip.vue";
import {
	BOOST_DURATION,
	BOOST_MAX,
	BOOST_TICK_DURATION,
	IS_DEV,
} from "@/config/constants";
import { ResourceType } from "@/game/entities/config/resources";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useSocketService from "@/hooks/useSocketService";
import Communicator from "@/libs/AppCommunicatior";
import { AdAnalyticsClickSource, AdAnalyticsType } from "@/libs/SocketService";
import router from "@/router";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import { Placements, RouteNames, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw } from "vue";

const emit = defineEmits(["closePopup"]);

const communicator = markRaw(Communicator.getInstance());
const socketService = markRaw(useSocketService());
const resourcesStore = useResourcesStore();

const userStore = useUserStore();
const { incomeBoostsUsedToday } = storeToRefs(userStore);
const runtimeStore = useRuntimeStore();

const { incomeBoostActive } = storeToRefs(runtimeStore);

const adsAvailable = computed((): boolean => {
	return communicator.isRewardedAdAvailable;
});

const close = (): void => {
	emit("closePopup");
};

const watchAd = (): void => {
	if (IS_DEV) {
		userStore.setIncomeBoostTicksRemaining(BOOST_TICK_DURATION);
		runtimeStore.setIncomeBoostActive(true);
		runtimeStore.setIncomeBoostAdActive(false);
		close();
		return;
	}
	if (communicator.isRewardedAdAvailable) {
		if (!activateBoost()) return;
		runtimeStore.setIncomeBoostAdActive(true);
		socketService.emitAdAnalyticsSave({
			type: AdAnalyticsType.INCOME_BOOST_AD,
			clickSource: AdAnalyticsClickSource.POPUP,
		});
	}

	communicator.playRewardedAd();
	close();
};

const rewardText = computed((): string => {
	return translate("ui.triple_your_income").replace(
		"{number}",
		`<span>${BOOST_DURATION}</span>`,
	);
});

const boostsRemaining = computed((): number => {
	return (
		BOOST_MAX +
		getUpgradeCurrentReplaceValueById(UpgradeIds.witch_lake) -
		(incomeBoostsUsedToday.value || 0)
	);
});

const activateBoost = (): boolean => {
	if (incomeBoostActive.value) return false;
	if (boostsRemaining.value > 0) {
		userStore.setIncomeBoostsUsedToday(
			(incomeBoostsUsedToday.value || 0) + 1,
		);
	} else {
		return false;
	}
	return true;
};

const useAdSkipper = (): void => {
	if (!resourcesStore.adSkipper) {
		router.push({
			name: RouteNames.SHOP,
			params: { scrollTo: ResourceType.AD_SKIPPER },
		});
		close();
		return;
	}
	if (activateBoost()) {
		resourcesStore.decrease(
			ResourceType.AD_SKIPPER,
			1,
			"used for income boost",
		);
		userStore.setIncomeBoostTicksRemaining(BOOST_TICK_DURATION);
		runtimeStore.setIncomeBoostActive(true);
		runtimeStore.setIncomeBoostAdActive(false);
	}
	close();
};
</script>

<style lang="scss" scoped>
.rewarded-popup {
	padding: 0.625rem 0.25rem;
	@include flex-col-center;
	gap: 0.5rem;
	&__icon {
		position: fixed;
		top: 4rem;
	}
	&__text {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.15rem;
		padding: 0.5rem;
		color: #372615;
		font-size: 0.906rem;
		& > span {
			font-weight: bold;
		}
	}
	&__btn {
		width: 15.625rem;
		&-divider {
			& > span {
				color: #372615;
				font-weight: bold;
				font-size: 1.063rem;
			}
		}
	}
	&__skippers {
		display: flex;
		align-items: center;
		gap: 0.15rem;
		color: #372615;
		font-size: 0.906rem;
		& > span {
			font-weight: bold;
		}
	}
	:deep(.rewarded-popup__text > span) {
		font-weight: bold;
	}
}
</style>
